import {ChangeDetectionStrategy, Component, computed, input, model, signal} from "@angular/core"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-numeric-field",
    templateUrl: "./numeric-field.component.html",
    styleUrls: ["./numeric-field.component.scss"],
})
export class NumericFieldComponent {
    readonly $value = model<number | undefined>(undefined, {alias: "value"})
    readonly $label = input<string>("", {alias: "label"})
    readonly $decimalPlaces = input<number>(2, {alias: "decimalPlaces"})
    readonly $placeholder = input<string | undefined>(undefined, {alias: "placeholder"})
    readonly $disabled = input(false, {alias: "disabled"})

    private readonly $editing = signal(false)
    private readonly $editString = signal<string | null>(null)

    // Deliberately a plain type="text" input (not type="number"): we need the parsed value to update
    // on every keystroke — not just on blur/commit — so a sibling linked field (see
    // LinkedDimensionInputComponent) is already correct before Tab can move focus to it. type="number"
    // fights this: it reports .value as "" for an in-progress entry that isn't yet a complete valid
    // number (e.g. a trailing "." with no digit after it), which clobbers the display and resets the
    // cursor to the start. A plain text field sidesteps that entirely; "," is also accepted as a
    // decimal separator since we lose type="number"'s (inconsistent, browser-dependent) locale handling.
    readonly $displayString = computed(() => {
        if (this.$editing()) {
            return this.$editString()
        }
        const value = this.$value()
        // guard against null too: some callers source this from GraphQL data typed as `number | null`
        return value == null ? "" : value.toFixed(this.$decimalPlaces())
    })

    protected onInput(valueStr: string): void {
        this.$editString.set(valueStr)
        const parsed = parseFloat(valueStr.replace(",", "."))
        if (isNaN(parsed)) return
        this.$value.set(parsed)
    }

    protected onFocus(): void {
        this.$editString.set(this.$displayString())
        this.$editing.set(true)
    }

    protected onBlur(): void {
        this.$editing.set(false)
        this.$editString.set(null)
    }
}
